import * as React from "react";

import { Alert } from "Components/ui";
import { BillingState, Invoice } from "api/billing";
import { cn } from "utils/cn";
import { formatDate, formatDateTime, todayIST, toDate } from "utils/format";

interface Step {
  key: string;
  label: string;
  when: string;
  done: boolean;
  tone: "info" | "warning" | "neutral" | "danger";
  note?: string;
}

const DOT_CLASSES: Record<Step["tone"], { done: string; line: string }> = {
  info: { done: "border-info bg-info", line: "bg-info/30" },
  warning: { done: "border-warning bg-warning", line: "bg-warning/30" },
  neutral: { done: "border-subtle-foreground bg-subtle-foreground", line: "bg-border" },
  danger: { done: "border-danger bg-danger", line: "bg-danger/30" },
};

interface FocusTimelineProps {
  invoice: Invoice;
  state: BillingState;
  now: number;
}

/** Issued → Warning from → Due → Read-only from (or "Warn only" / "Enforcement off"). */
const FocusTimeline: React.FC<FocusTimelineProps> = ({ invoice, state, now }) => {
  const reached = (value: string | number | null | undefined) => {
    const date = toDate(value);
    return Boolean(date && date.getTime() <= now);
  };
  const enforcementOff = state.enforcement === "off";
  const steps: Step[] = [
    {
      key: "issued",
      label: "Issued",
      when: invoice.issuedAt ? formatDate(invoice.issuedAt) : "Not yet",
      done: reached(invoice.issuedAt),
      tone: "info",
    },
    {
      key: "warning",
      label: "Warning from",
      when: state.warnAt ? formatDateTime(state.warnAt) : "—",
      done: reached(state.warnAt),
      tone: "warning",
      note: enforcementOff ? "Not shown: enforcement is off" : undefined,
    },
    {
      key: "due",
      label: "Due",
      when: formatDate(invoice.dueDate),
      done: todayIST(now) >= invoice.dueDate,
      tone: "warning",
    },
    state.enforcement === "read_only"
      ? {
          key: "lock",
          label: "Read-only from",
          when: state.lockAt ? formatDateTime(state.lockAt) : "—",
          done: reached(state.lockAt),
          tone: "danger",
          note: "After at least 72 h of visible warning in TeleOpd",
        }
      : {
          key: "lock",
          label: state.enforcement === "warn_only" ? "Warn only" : "Enforcement off",
          when: state.enforcement === "warn_only" ? "Never read-only" : "TeleOpd shows nothing",
          done: false,
          tone: "neutral",
        },
  ];

  return (
    <div>
      {state.paused && (
        <Alert tone="info" className="mb-3">
          Paused until {formatDateTime(state.pausedUntil)}: TeleOpd shows nothing until then.
        </Alert>
      )}
      <ol className="grid grid-cols-1 gap-4 sm:grid-cols-4 sm:gap-2">
        {steps.map((step, index) => {
          const classes = DOT_CLASSES[step.tone];
          return (
            <li key={step.key} className="relative flex gap-3 sm:flex-col sm:gap-2">
              <div className="flex items-center sm:w-full">
                <span
                  className={cn(
                    "h-4 w-4 shrink-0 rounded-full border-2",
                    step.done ? classes.done : "border-input bg-surface"
                  )}
                  aria-hidden="true"
                />
                {index < steps.length - 1 && (
                  <span
                    className={cn(
                      "ml-2 hidden h-0.5 flex-1 rounded-full sm:block",
                      step.done ? classes.line : "bg-border"
                    )}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">{step.label}</p>
                <p
                  className={cn(
                    "text-sm",
                    step.done ? "font-semibold text-foreground" : "text-muted-foreground"
                  )}
                >
                  {step.when}
                </p>
                {step.note && <p className="mt-0.5 text-xs text-subtle-foreground">{step.note}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default FocusTimeline;
